/**
 * Central registry + runtime store for dev feature flags and tweaks (BUILD-379).
 *
 * This module is the SINGLE source of truth: the dev panel renders from `DEFS`,
 * so adding an entry here makes it appear in the panel automatically. It is also
 * the ONLY place allowed to touch `localStorage` for flag/tweak keys — every read
 * goes through `getFlag`/`getValue` and every write through `setValue`. A Vitest
 * architecture guard (`tests/feature-flags-guard.test.ts`) enforces that funnel
 * plus key naming, so an unregistered flag is literally unreadable.
 *
 * Storage keys are namespaced by kind: booleans live under `ff.<key>`, valued
 * tweaks under `tweak.<key>`. The two legacy unprefixed Tahiti keys are migrated
 * on load (see `migrateLegacyKeys`) so existing dev settings survive the rename.
 *
 * Immediacy is hybrid: each entry declares a `behavior`. `reactive` entries update
 * live via a `useSyncExternalStore` subscription (and across tabs via the `storage`
 * event); `reload` entries persist then reload the page, because their consumers
 * (the MSW worker in `mocks/browser.ts`) only read the flag once at boot.
 */

import { useSyncExternalStore } from 'react';

// ── Registry types ───────────────────────────────────────────────────────────

/** How a change takes effect. See module docblock. */
export type FlagBehavior = 'reactive' | 'reload';

type BaseDef = {
  /** Storage key WITHOUT the kind prefix. Unique across the registry, snake_case. */
  key: string;
  /** Human label shown in the panel. */
  label: string;
  /** One-line explanation shown under the label. */
  description: string;
  behavior: FlagBehavior;
};

export type BooleanDef = BaseDef & { type: 'boolean'; default: boolean };
export type NumberDef = BaseDef & {
  type: 'number';
  default: number;
  min?: number;
  max?: number;
  step?: number;
};
export type StringDef = BaseDef & { type: 'string'; default: string };
export type EnumDef = BaseDef & { type: 'enum'; default: string; options: readonly string[] };

/** A single registry entry. Booleans render in the Flags tab, the rest in Tweaks. */
export type Def = BooleanDef | NumberDef | StringDef | EnumDef;

/** A parsed flag/tweak value. */
export type FlagValue = boolean | number | string;

// ── The registry ─────────────────────────────────────────────────────────────

/**
 * The source of truth. Add an entry and it shows up in the dev panel.
 * Booleans → Flags tab; number/string/enum → Tweaks tab.
 */
export const DEFS = [
  {
    type: 'enum',
    key: 'tahiti_dev_target',
    default: 'prod',
    options: ['prod', 'devdb'],
    behavior: 'reload',
    label: 'Tahiti dev target',
    description:
      'Which Tahiti backend the Vite dev proxy forwards /api to. Prod (db.patrickmathieu.net) is the default; devdb is the fallback sandbox. Dev server only — deployed environments pick their target via API_PROXY_TARGET/TAHITI_BASE_URL.',
  },
  {
    type: 'boolean',
    key: 'tahiti_mock_reads',
    default: false,
    behavior: 'reload',
    label: 'Mock reads',
    description: 'Serve reads (and auth) from the MSW mock layer instead of live Tahiti.',
  },
  {
    type: 'boolean',
    key: 'tahiti_real_writes',
    default: true,
    behavior: 'reload',
    label: 'Real writes',
    description:
      'Send writes to the real Tahiti backend (meta/object). On by default; turn off to mock writes for offline dev.',
  },
  {
    type: 'boolean',
    key: 'dev_panel_hidden',
    default: false,
    behavior: 'reactive',
    label: 'Hide dev panel button',
    description: 'Hide the floating launcher. Reopen the panel with Ctrl/Cmd+Shift+.',
  },
  {
    type: 'enum',
    key: 'mosaic_card_style',
    default: 'tint',
    options: ['tint', 'border'],
    behavior: 'reactive',
    label: 'Mosaic card accent',
    description:
      'How mosaic cards show their dominant noyau colour: a tinted background, or a coloured border.',
  },
  {
    type: 'enum',
    key: 'function_letter_style',
    default: 'chip',
    options: ['chip', 'bare'],
    behavior: 'reactive',
    label: 'Function letter style',
    description:
      'How the fonction (G/P/S) letter renders: a deep letter on a light-tint disc, or the bare glyph filled with the pale tint colour.',
  },
  {
    type: 'enum',
    key: 'tag_picker_style',
    default: 'modal',
    options: ['modal', 'popover', 'tabbed'],
    behavior: 'reactive',
    label: 'Tag picker style',
    description:
      'How the per-category tag picker renders: a centered modal, a popover anchored under the + button, or a single tabbed modal covering every category.',
  },
] as const satisfies readonly Def[];

/** Booleans (Flags tab). */
export const FLAG_DEFS: readonly Def[] = DEFS.filter((def) => def.type === 'boolean');
/** Valued tweaks (Tweaks tab). */
export const TWEAK_DEFS: readonly Def[] = DEFS.filter((def) => def.type !== 'boolean');

// ── Storage key namespacing ───────────────────────────────────────────────────

const KIND_PREFIX = {
  boolean: 'ff.',
  number: 'tweak.',
  string: 'tweak.',
  enum: 'tweak.',
} as const satisfies Record<Def['type'], string>;

/** Full localStorage key for a def, including its kind prefix. */
export function storageKey(def: Def): string {
  return KIND_PREFIX[def.type] + def.key;
}

function defByKey(key: string): Def | undefined {
  return DEFS.find((def) => def.key === key);
}

// ── localStorage access (the only place that touches it for flag keys) ─────────

function readRaw(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function writeRaw(key: string, value: string): void {
  try {
    globalThis.localStorage?.setItem(key, value);
  } catch {
    // localStorage may be unavailable (private mode, SSR) — degrade silently.
  }
}

function removeRaw(key: string): void {
  try {
    globalThis.localStorage?.removeItem(key);
  } catch {
    // ignore
  }
}

function parseValue(def: Def, raw: string | null): FlagValue {
  if (raw === null) return def.default;
  switch (def.type) {
    case 'boolean':
      return raw === 'true';
    case 'number': {
      const n = Number(raw);
      return Number.isFinite(n) ? n : def.default;
    }
    case 'enum':
      return def.options.includes(raw) ? raw : def.default;
    case 'string':
      return raw;
  }
}

// ── Reactive store ─────────────────────────────────────────────────────────────

type Listener = () => void;
const listeners = new Set<Listener>();

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  // Reflect changes made in other tabs/windows.
  const onStorage = (): void => listener();
  globalThis.addEventListener?.('storage', onStorage);
  return () => {
    listeners.delete(listener);
    globalThis.removeEventListener?.('storage', onStorage);
  };
}

function reloadPage(): void {
  try {
    globalThis.location?.reload();
  } catch {
    // No location in this environment (e.g. tests) — nothing to reload.
  }
}

// ── Public read/write API ──────────────────────────────────────────────────────

/** Current parsed value for a def (default when unset/invalid). */
export function getValue(def: Def): FlagValue {
  return parseValue(def, readRaw(storageKey(def)));
}

/** Boolean flag accessor by key. Returns `false` for unknown / non-boolean keys. */
export function getFlag(key: string): boolean {
  const def = defByKey(key);
  if (!def || def.type !== 'boolean') return false;
  return parseValue(def, readRaw(storageKey(def))) === true;
}

/** Tweak accessor by key. Returns the def default for unknown keys. */
export function getTweak(key: string): FlagValue | undefined {
  const def = defByKey(key);
  return def ? getValue(def) : undefined;
}

/**
 * Persist a new value for a def and notify subscribers. If the def is a `reload`
 * behavior, the page reloads immediately afterwards (its consumers only read at boot).
 */
export function setValue(def: Def, value: FlagValue): void {
  writeRaw(storageKey(def), String(value));
  syncDevTargetCookie(def, value);
  emit();
  if (def.behavior === 'reload') reloadPage();
}

// ── Dev-server bridge for `tahiti_dev_target` ───────────────────────────────

/**
 * The Vite dev proxy (vite.config.ts) runs in a separate Node process that
 * can't see this tab's localStorage, so `tahiti_dev_target` also mirrors into
 * a plain cookie — sent automatically on every same-origin `/api/...` request
 * — which the proxy reads (see `pickTahitiTarget` in vite.config.ts) to pick
 * the upstream per request. No-op for every other def.
 */
function syncDevTargetCookie(def: Def, value: FlagValue): void {
  if (def.key !== 'tahiti_dev_target') return;
  try {
    document.cookie = `tahiti_dev_target=${value};path=/;max-age=31536000`;
  } catch {
    // No document in this environment (tests/SSR) — nothing to sync.
  }
}

/** Set a boolean flag by key. No-op for unknown / non-boolean keys. */
export function setFlag(key: string, value: boolean): void {
  const def = defByKey(key);
  if (def && def.type === 'boolean') setValue(def, value);
}

/** Clear every flag/tweak back to its default and notify subscribers. */
export function resetAll(): void {
  for (const def of DEFS) removeRaw(storageKey(def));
  emit();
}

/** Whether any registered entry uses `reload` behavior (used to decide reset UX). */
export function hasReloadDef(): boolean {
  return DEFS.some((def) => def.behavior === 'reload');
}

// ── React hooks ─────────────────────────────────────────────────────────────────

/** Subscribe to a def's value reactively. */
export function useValue(def: Def): FlagValue {
  return useSyncExternalStore(
    subscribe,
    () => getValue(def),
    () => def.default,
  );
}

/** Subscribe to a boolean flag reactively by key. */
export function useFlag(key: string): boolean {
  const def = defByKey(key);
  return useSyncExternalStore(
    subscribe,
    () =>
      def && def.type === 'boolean' ? parseValue(def, readRaw(storageKey(def))) === true : false,
    () => (def && def.type === 'boolean' ? def.default : false),
  );
}

/** Subscribe to a tweak's value (number/string/enum) reactively by key. */
export function useTweak(key: string): FlagValue | undefined {
  const def = defByKey(key);
  return useSyncExternalStore(
    subscribe,
    () => (def ? getValue(def) : undefined),
    () => def?.default,
  );
}

// ── Legacy key migration ──────────────────────────────────────────────────────

/**
 * One-time migration: the two original Tahiti flags lived under unprefixed keys
 * (`tahiti_mock_reads`, `tahiti_real_writes`). Copy any legacy value into the new
 * prefixed key (without clobbering an existing one) and drop the legacy key, so a
 * dev's current toggles survive the rename. Safe to call repeatedly.
 */
export function migrateLegacyKeys(): void {
  for (const def of DEFS) {
    const legacyValue = readRaw(def.key);
    if (legacyValue === null) continue;
    if (readRaw(storageKey(def)) === null) writeRaw(storageKey(def), legacyValue);
    removeRaw(def.key);
  }
}

// Run the migration as soon as the module loads, before any consumer (e.g. the
// MSW worker in mocks/browser.ts) reads a flag.
migrateLegacyKeys();

// ── Backward-compatible accessors ──────────────────────────────────────────────
// Kept so mocks/browser.ts and components/dev/mode-badge.tsx need no changes.

/** Whether reads should be served from the MSW mock layer. */
export function mockReadsEnabled(): boolean {
  return getFlag('tahiti_mock_reads');
}

/** Whether writes should hit the real Tahiti backend. */
export function realWritesEnabled(): boolean {
  return getFlag('tahiti_real_writes');
}
